import { useEffect, useMemo, useState } from 'react';
import {
  BaseTransaction,
  Currency,
  Filters,
  PaymentType,
  ResponseError,
  responseErrorsResolver,
  SchemaFilterType,
  SelectItemProps,
  useFilters,
  useGetList,
  useQueryState,
} from '@ui/src';
import { useAppDispatch } from '@store';
import isEqual from 'lodash.isequal';
import {
  useLazyGetFintracIncomingTransactionsQuery,
  useLazyGetFintracPaymentTypesQuery,
  useAddTransactionsToFintracReportMutation,
  useLazyGetFintracReportQuery,
  useLazyGetFintracReportTransactionsQuery,
} from '../store/services';
import { setNotificationVisible } from '../../../../../../store/slice';
import { filtersSchema, schema } from '../variables/incoming-transactions';
import useGetAccountsAutocompleteList from './use-accounts-autocomplete';
import { PaymentType as PaymentTypeProps } from '../../warning-report/store/types';
import { clearFintracTranscationsState } from '../store/slices/list';
import formatIncomingList from '../helpers/fornat-incoming-transactions';

const useAddTransactionsSidepanel = ({
  transactions,
  setAddTransactionsSidepanelVisible,
  paymentTypes,
  currencies,
  currenciesList,
  addTransactionsSidepanelVisible,
  selectedReportTransferDate,
  setSelectedReportTransferDate,
  type,
  selectedReportId,
}: {
  selectedReportTransferDate?: string;
  transactions?: BaseTransaction[];
  setAddTransactionsSidepanelVisible: (data: boolean) => void;
  setSelectedReportTransferDate: (data: string) => void;
  paymentTypes?: PaymentTypeProps[];
  currencies?: SelectItemProps[];
  currenciesList?: Currency[];
  addTransactionsSidepanelVisible: boolean;
  type: 'eftr' | 'lvctr';
  selectedReportId?: string;
}) => {
  const [selectedTransactionsIds, setSelectedTransactionsIds] = useState<
    string[]
  >([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [transferDate, setTransferDate] = useState<{
    transferDateFrom: string;
    transferDateTo: string;
  }>({
    transferDateFrom: '',
    transferDateTo: '',
  });
  const [availablePaymentTypes, setAvailablePaymentTypes] = useState<
    PaymentTypeProps[]
  >([]);
  const [globalError, setGlobalError] = useState('');

  const [getTransactionsList, { isLoading, isFetching }] =
    useLazyGetFintracIncomingTransactionsQuery();
  const [getPaymentTypes] = useLazyGetFintracPaymentTypesQuery();
  const [addTransactionsToReport, { isLoading: requestInProgress }] =
    useAddTransactionsToFintracReportMutation();
  const [getFintracReportTransactions] =
    useLazyGetFintracReportTransactionsQuery();
  const [getFintracReport] = useLazyGetFintracReportQuery();
  const [includedTransactionsIds, setIncludedTransactionsIds] = useState<
    string[]
  >([]);

  const dispatch = useAppDispatch();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(
    schema,
    true,
  );

  const {
    autocompleteList: recipientAccounts,
    onSearch: onSearchRecipientAccounts,
    onClear,
  } = useGetAccountsAutocompleteList(
    '&isClientAccount=true&sortProp=fullNumber&sortDir=asc',
    'fullNumber',
  );

  const onRowsSelect = (ids: string[]) => {
    setSelectedTransactionsIds(ids);
  };

  const { getListAction, setPage, onFilter, onSort, errorList } =
    useGetList<BaseTransaction>({
      getList: getTransactionsList,
      onFilterChange,
      onSortChange,
      schema,
      withoutChangeSearchParams: true,
    });

  const onTableFilter = (values: Filters) => {
    const filterValues: Filters = {
      ...values,
      take: {
        value: ['30'],
        type: SchemaFilterType.STRING,
      },
      baseellaStatus: {
        value: ['ARCH'],
        type: SchemaFilterType.STRING,
      },
      transferDateFrom: {
        value: [transferDate.transferDateFrom],
        type: SchemaFilterType.STRING,
      },
      transferDateTo: {
        value: [transferDate.transferDateTo],
        type: SchemaFilterType.STRING,
      },
      paymentType: {
        value: availablePaymentTypes?.map((item) => item.paymentType),
        type: SchemaFilterType.STRING,
      },
    };

    onFilter(filterValues);
  };

  const onTableSetPage = ({ id, take }: { id: string; take: string }) => {
    const filterValues: Filters = {
      ...filters,
      take: {
        value: ['30'],
        type: SchemaFilterType.STRING,
      },
      baseellaStatus: {
        value: ['ARCH'],
        type: SchemaFilterType.STRING,
      },
      transferDateFrom: {
        value: [transferDate.transferDateFrom],
        type: SchemaFilterType.STRING,
      },
      transferDateTo: {
        value: [transferDate.transferDateTo],
        type: SchemaFilterType.STRING,
      },
      paymentType: {
        value: availablePaymentTypes?.map((item) => item.paymentType),
        type: SchemaFilterType.STRING,
      },
    };

    setPage({ id, take, filters: filterValues });
  };

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    if (name === 'recipientAccountNumber') {
      onSearchRecipientAccounts(searchAutocomplete);
    }
  };

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
    onSearchAutocomplete,
  });

  const formattedData = useMemo(() => {
    let nextTransactions = transactions;

    if (includedTransactionsIds?.length) {
      nextTransactions = nextTransactions?.map((item) => {
        const result = { ...item };

        if (includedTransactionsIds?.includes(item.id)) {
          result.deletedAt = new Date().toISOString();
        }

        return result;
      });
    }

    const data = formatIncomingList({
      list: nextTransactions as BaseTransaction[],
      currenciesList,
      paymentTypes,
    });

    return data;
  }, [transactions, currenciesList, paymentTypes, includedTransactionsIds]);

  const handleClose = () => {
    setAddTransactionsSidepanelVisible(false);
    setSelectedTransactionsIds([]);
    setSelectedReportTransferDate('');
    dispatch(clearFintracTranscationsState());
    setGlobalError('');
    setTransferDate({
      transferDateFrom: '',
      transferDateTo: '',
    });
    setIncludedTransactionsIds([]);
  };

  const onSubmit = async () => {
    setGlobalError('');
    setIncludedTransactionsIds([]);

    const transactionsList: {
      regulatoryReportId: string;
      transactionId: string;
    }[] = [];

    selectedTransactionsIds?.forEach((item) => {
      transactionsList.push({
        regulatoryReportId: selectedReportId as string,
        transactionId: item,
      });
    });

    const addTransactionsResponse = (await addTransactionsToReport({
      items: transactionsList,
    })) as { error?: ResponseError };

    if (addTransactionsResponse?.error) {
      responseErrorsResolver({
        errors: addTransactionsResponse?.error,
        setError: () => {},
        setGlobalError,
      });
    } else {
      handleClose();

      dispatch(
        setNotificationVisible({
          visible: true,
          text: 'The transactions have been added to the report',
          type: '',
          loading: false,
        }),
      );

      setTimeout(() => {
        getFintracReportTransactions(
          `?regulatoryReportId=${selectedReportId}&take=15`,
        );
        getFintracReport(selectedReportId as string);
      }, 500);

      setTimeout(() => {
        dispatch(
          setNotificationVisible({
            visible: false,
            text: '',
            type: '',
          }),
        );
      }, 3000);
    }
  };

  useEffect(() => {
    setValues('recipientAccountNumber', recipientAccounts);
    setValues('currencyId', currencies as SelectItemProps[]);
  }, [recipientAccounts, currencies, availablePaymentTypes]);

  useEffect(() => {
    if (paymentTypes) {
      setAvailablePaymentTypes(
        type === 'eftr'
          ? paymentTypes?.filter(
              (item) => item.paymentType !== PaymentType.CRYPTO_ASSET_PAYMENT,
            )
          : paymentTypes?.filter(
              (item) => item.paymentType === PaymentType.CRYPTO_ASSET_PAYMENT,
            ),
      );
    }
  }, [paymentTypes, type]);

  useEffect(() => {
    if (selectedReportTransferDate && addTransactionsSidepanelVisible) {
      if (selectedReportTransferDate) {
        const dateFrom = new Date(selectedReportTransferDate);
        const dateTo = new Date(selectedReportTransferDate);
        dateFrom.setHours(0);
        dateFrom.setMinutes(0);
        dateFrom.setSeconds(0);
        dateTo.setHours(23);
        dateTo.setMinutes(59);
        dateTo.setSeconds(59);

        setTransferDate({
          transferDateFrom: new Date(dateFrom).toISOString(),
          transferDateTo: new Date(dateTo).toISOString(),
        });
      }
    }
  }, [selectedReportTransferDate, addTransactionsSidepanelVisible]);

  useEffect(() => {
    if (!paymentTypes) {
      getPaymentTypes();
    }
  }, [paymentTypes]);

  useEffect(() => {
    if (
      !transactions &&
      selectedReportTransferDate &&
      addTransactionsSidepanelVisible &&
      transferDate.transferDateFrom &&
      transferDate.transferDateTo
    ) {
      const filtersValues: any = {
        take: {
          value: ['30'],
          type: SchemaFilterType.STRING,
        },
        baseellaStatus: {
          value: ['ARCH'],
          type: SchemaFilterType.STRING,
        },
        transferDateFrom: {
          value: [transferDate.transferDateFrom],
          type: SchemaFilterType.STRING,
        },
        transferDateTo: {
          value: [transferDate.transferDateTo],
          type: SchemaFilterType.STRING,
        },
        paymentType: {
          value: availablePaymentTypes?.map((item) => item.paymentType),
          type: SchemaFilterType.STRING,
        },
      };

      getListAction({
        filters: filtersValues,
        sort,
      });
    }
  }, [
    transactions,
    selectedReportTransferDate,
    addTransactionsSidepanelVisible,
    transferDate,
  ]);

  useEffect(() => {
    if (transactions) {
      if (
        !filters?.cursor?.value?.length &&
        (!selectedTransactionsIds?.length ||
          !isEqual(
            transactions?.map((item) => item.id),
            selectedTransactionsIds,
          )) &&
        transactions?.length
      ) {
        setSelectedTransactionsIds(transactions?.map((item) => item.id));
      } else if (filters?.cursor?.value?.length) {
        const nextSelectedTransactionsIds = ([] as string[]).concat(
          selectedTransactionsIds,
        );

        transactions?.forEach((item) => {
          if (!nextSelectedTransactionsIds?.find((id) => item.id === id)) {
            nextSelectedTransactionsIds.push(item.id);
          }
        });

        setSelectedTransactionsIds(nextSelectedTransactionsIds);
      }
    }
  }, [transactions, filters]);

  useEffect(() => {
    if (globalError) {
      const globalErrorArray = globalError?.split(':');

      if (globalErrorArray?.length && globalErrorArray?.length > 1) {
        const transactionsIds = globalErrorArray[1]
          .split(',')
          ?.map((item) => item?.trim());

        if (transactionsIds?.length) {
          setIncludedTransactionsIds(transactionsIds);
        }
      }
    }
  }, [globalError]);

  return {
    handleClose,
    selectedOrganizationId,
    setSelectedOrganizationId,
    formattedData,
    onSort,
    filters,
    filtersComponent,
    sort,
    handleSubmit,
    isLoading,
    isFetching,
    errorList,
    onClear,
    onRowsSelect,
    selectedTransactionsIds,
    onTableSetPage,
    onTableFilter,
    globalError,
    requestInProgress,
    onSubmit,
  };
};

export default useAddTransactionsSidepanel;
